import React, { useEffect, useMemo, useState } from "react";
import { api, API, codeClasses, PRIORITY_CODES } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartBar, Users, Calendar, TrendUp, TrendDown, Warning, FileCsv, FilePdf, Gear, Funnel, X, ArrowClockwise } from "@phosphor-icons/react";

const CODE_COLORS = {
  rosso: "#dc2626", arancione: "#f97316", azzurro: "#0ea5e9", verde: "#10b981", bianco: "#e5e7eb",
};

function CodeBar({ counts, total }) {
  if (!total) return <div className="text-xs text-slate-400">nessun dato</div>;
  return (
    <div className="flex h-6 rounded overflow-hidden border border-slate-200">
      {PRIORITY_CODES.map((c) => {
        const v = counts[c.code] || 0;
        if (!v) return null;
        const w = (v / total) * 100;
        return (
          <div key={c.code} title={`${c.label}: ${v}`} style={{ width: `${w}%`, background: CODE_COLORS[c.code] }}
               className={`text-[10px] font-semibold flex items-center justify-center ${c.code === "bianco" ? "text-slate-800" : "text-white"}`}>
            {v >= 3 && v}
          </div>
        );
      })}
    </div>
  );
}

function Delta({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">—</span>;
  const positive = value >= 0;
  const Arrow = positive ? TrendUp : TrendDown;
  const color = positive ? "text-emerald-700" : "text-red-700";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Arrow size={12} weight="bold" /> {positive ? "+" : ""}{value}%
    </span>
  );
}

export default function StatsPage() {
  const [days, setDays] = useState("30");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [nurseEmail, setNurseEmail] = useState("");
  const [protocolId, setProtocolId] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ modified_alert_threshold: 0.2 });
  const [thresholdInput, setThresholdInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [protocols, setProtocols] = useState([]);

  const buildQs = (extra = "") => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", `${dateFrom}T00:00:00+00:00`);
      if (dateTo) p.set("date_to", `${dateTo}T23:59:59+00:00`);
    } else {
      p.set("days", days);
    }
    if (nurseEmail) p.set("nurse_email", nurseEmail);
    if (protocolId) p.set("protocol_id", protocolId);
    return p.toString() + (extra ? `&${extra}` : "");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, pl] = await Promise.all([
        api.get(`/stats/codes?${buildQs()}`),
        api.get(`/config`),
        protocols.length === 0 ? api.get(`/protocols`) : Promise.resolve({ data: protocols }),
      ]);
      setStats(s.data);
      setConfig(c.data);
      setThresholdInput(String(Math.round((c.data.modified_alert_threshold || 0) * 100)));
      setProtocols(pl.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, dateFrom, dateTo, nurseEmail, protocolId]);

  const resetFilters = () => { setDateFrom(""); setDateTo(""); setNurseEmail(""); setProtocolId(""); };

  const saveThreshold = async () => {
    const pct = Math.max(0, Math.min(100, Number(thresholdInput) || 0));
    const { data } = await api.put(`/config`, { modified_alert_threshold: pct / 100 });
    setConfig(data);
    toast.success(`Soglia salvata a ${pct}%`);
    setShowSettings(false);
  };

  const resetSplash = async () => {
    if (!window.confirm("Forzare lo splash all'apertura del prossimo turno per tutti gli utenti?")) return;
    const { data } = await api.post(`/config/reset-splash`);
    toast.success(`Splash forzato per il prossimo accesso (versione ${data.splash_reset_version})`);
  };

  const modifiedRatio = useMemo(() => {
    if (!stats || !stats.total_triages) return 0;
    return stats.modified_count / stats.total_triages;
  }, [stats]);
  const thresholdExceeded = stats && modifiedRatio > (config.modified_alert_threshold || 0);

  const download = (fmt) => {
    window.open(`${API}/stats/codes/export?${buildQs(`fmt=${fmt}`)}`, "_blank");
  };

  const chartData = (stats?.by_day || []).map((d) => ({ day: d.day.slice(5), ...d }));

  return (
    <div>
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-xl font-semibold flex items-center gap-2">
            <ChartBar size={22} weight="fill" /> Statistiche triage
          </div>
          <div className="text-xs text-slate-500">Delta calcolati rispetto al periodo precedente equivalente</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowFilters((v) => !v)} data-testid="stats-filters-toggle" className="min-h-[44px]">
            <Funnel size={16} className="mr-2" /> Filtri {(dateFrom || dateTo || nurseEmail || protocolId) && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold">●</span>}
          </Button>
          <Button variant="outline" onClick={() => setShowSettings((v) => !v)} data-testid="stats-settings-toggle" className="min-h-[44px]">
            <Gear size={16} className="mr-2" /> Soglia alert
          </Button>
          <Button variant="outline" onClick={() => download("csv")} data-testid="export-csv-button" className="min-h-[44px]">
            <FileCsv size={16} className="mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="export-pdf-button" className="min-h-[44px]">
            <FilePdf size={16} className="mr-2" /> PDF
          </Button>
          <Select value={days} onValueChange={setDays} disabled={!!(dateFrom || dateTo)}>
            <SelectTrigger className="w-40 min-h-[44px]" data-testid="stats-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              <SelectItem value="365">Ultimo anno</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Data inizio</div>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" className="w-44" />
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Data fine</div>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" className="w-44" />
              </div>
              <div className="min-w-[220px]">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Infermiere</div>
                <Select value={nurseEmail || "all"} onValueChange={(v) => setNurseEmail(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-nurse"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli infermieri</SelectItem>
                    {(stats?.by_nurse || []).map((n) => (
                      <SelectItem key={n.nurse_email} value={n.nurse_email}>{n.nurse_name} · {n.nurse_email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[260px] flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Protocollo</div>
                <Select value={protocolId || "all"} onValueChange={(v) => setProtocolId(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-protocol"><SelectValue placeholder="Tutti" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i protocolli</SelectItem>
                    {protocols.map((p) => (
                      <SelectItem key={p.protocol_id} value={p.protocol_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(dateFrom || dateTo || nurseEmail || protocolId) && (
                <Button variant="ghost" onClick={resetFilters} data-testid="filter-reset" className="text-slate-600">
                  <X size={16} className="mr-1" /> Azzera filtri
                </Button>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              I filtri si applicano sia alla dashboard che agli export CSV/PDF.
            </div>
          </div>
        )}

        {showSettings && (
          <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Soglia di alert (% codici modificati)</div>
              <div className="flex items-center gap-2">
                <Input type="number" min={0} max={100} className="w-32" value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} data-testid="threshold-input" />
                <span className="text-sm text-slate-500">%</span>
                <Button onClick={saveThreshold} data-testid="save-threshold" className="bg-slate-900 hover:bg-slate-800">Salva</Button>
              </div>
            </div>
            <div className="text-xs text-slate-500 max-w-md">
              Se la percentuale di triage con codice assegnato diverso da quello suggerito supera la soglia, viene mostrato un banner di alert. Attualmente <b>{Math.round((config.modified_alert_threshold || 0) * 100)}%</b>.
            </div>
            <div className="ml-auto flex flex-col gap-1 items-end">
              <Button
                variant="outline"
                onClick={resetSplash}
                data-testid="reset-splash-button"
                className="min-h-[40px]"
                title="Forza lo splash al prossimo caricamento su tutti i client"
              >
                <ArrowClockwise size={16} className="mr-2" /> Reset splash (cambio turno)
              </Button>
              <div className="text-[11px] text-slate-500 max-w-[280px] text-right">
                Utile per contrassegnare il cambio turno: al prossimo caricamento tutti i client mostreranno lo splash.
              </div>
            </div>
          </div>
        )}

        {loading && <div className="text-sm text-slate-500">Caricamento…</div>}

        {stats && thresholdExceeded && (
          <div className="rounded-md border border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="threshold-alert">
            <Warning size={22} weight="fill" className="text-red-600 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-800">Scostamento sopra soglia</div>
              <div className="text-sm text-red-700">
                {stats.modified_count} triage su {stats.total_triages} ({Math.round(modifiedRatio * 100)}%) hanno un codice assegnato diverso dal suggerito.
                Soglia configurata: {Math.round((config.modified_alert_threshold || 0) * 100)}%.
              </div>
            </div>
          </div>
        )}

        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="p-4 rounded-md bg-white border border-slate-200">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Triage totali</div>
                <div className="font-mono text-3xl mt-1" data-testid="stats-total">{stats.total_triages}</div>
                <div className="mt-1"><Delta value={stats.deltas?.total} /></div>
              </div>
              {PRIORITY_CODES.map((c) => (
                <div key={c.code} className="p-4 rounded-md border" style={{ background: c.code === "bianco" ? "#fff" : CODE_COLORS[c.code] + "1A", borderColor: CODE_COLORS[c.code] + "55" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: CODE_COLORS[c.code] }}>{c.label}</div>
                  <div className="font-mono text-3xl mt-1" style={{ color: c.code === "bianco" ? "#0f172a" : CODE_COLORS[c.code] }}>
                    {stats.by_code[c.code] || 0}
                  </div>
                  <div className="mt-1"><Delta value={stats.deltas?.by_code?.[c.code]} /></div>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="font-heading text-lg font-semibold flex items-center gap-2">
                  <Calendar size={18} /> Andamento giornaliero
                </div>
                <div className="text-xs text-slate-500">
                  {stats.modified_count} codici modificati ({Math.round(modifiedRatio * 100)}%)
                </div>
              </div>
              {chartData.length === 0 ? (
                <div className="text-sm text-slate-500 py-8 text-center">Nessun triage nel periodo selezionato.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {PRIORITY_CODES.map((c) => (
                      <Bar key={c.code} dataKey={c.code} stackId="a" fill={CODE_COLORS[c.code]} name={c.label} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                <ChartBar size={18} /> Distribuzione per protocollo
              </div>
              <div className="space-y-3">
                {stats.by_protocol.slice(0, 20).map((row) => (
                  <div key={row.protocol_name} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-4 text-sm truncate">{row.protocol_name}</div>
                    <div className="col-span-1 font-mono text-sm text-slate-600">{row.total}</div>
                    <div className="col-span-7">
                      <CodeBar counts={row} total={row.total} />
                    </div>
                  </div>
                ))}
                {stats.by_protocol.length === 0 && (
                  <div className="text-sm text-slate-500">Nessun dato</div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                <Users size={18} /> Distribuzione per infermiere
              </div>
              <div className="space-y-3">
                {stats.by_nurse.map((row) => (
                  <div key={row.nurse_email} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-4 text-sm truncate">
                      <div className="font-medium">{row.nurse_name}</div>
                      <div className="text-xs text-slate-500 truncate">{row.nurse_email}</div>
                    </div>
                    <div className="col-span-1 font-mono text-sm text-slate-600">{row.total}</div>
                    <div className="col-span-7">
                      <CodeBar counts={row} total={row.total} />
                    </div>
                  </div>
                ))}
                {stats.by_nurse.length === 0 && (
                  <div className="text-sm text-slate-500">Nessun dato</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
